import { makeStyles } from 'tss-react/mui';

export const useSplitsViewStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
    boxShadow: theme.shadows[7],
    borderRadius: 12,
    flex: '1 1 auto',
  },
  rootSeries: {
    boxShadow: theme.shadows[1],
    width: '100%',
  },
  titleIconGroup: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: '8px 24px 0px 24px',
    borderBottom: `1px solid ${theme.palette.border.main}`,
  },
  titleIconGroupSeries: {
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.border.light}`,
  },
  titleWithIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  repeatIcon: {
    height: 18,
    width: 18,
    color: theme.palette.icon.light,
  },
  toggleButtons: {
    color: theme.palette.primary.main,
    borderRadius: 22,
    marginRight: -14,
  },
  editButton: {
    padding: '6px 8px',
    minWidth: 'unset',
    textTransform: 'none',
    ...theme.typography.paragraphMedium,
  },
  preview: {
    width: '100%',
    textAlign: 'left',
    fontWeight: 500,
  },
  /** Outer split row (transaction detail): vertical stack of line 1 / tags / memo */
  rowRootDetail: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1),
    margin: '12px 12px 4px',
    '&:last-child': {
      marginBottom: theme.spacing(2),
    },
  },
  /** Series: same vertical structure, padding from container */
  splitItemSeriesBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2, 0, 2),
    '&:last-child': {
      marginBottom: theme.spacing(2),
    },
  },
  /**
   * Row 1: Category (+ transfer) | Client & project | Exclusions + amount, billable below amount.
   * Grid collapses to one column on small screens.
   */
  splitRowLine1: {
    display: 'grid',
    gap: theme.spacing(2),
    alignItems: 'start',
    width: '100%',
    minWidth: 0,
  },
  splitRowLine1ThreeCol: {
    gridTemplateColumns: 'minmax(0, 1fr) minmax(140px, 237px) auto',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  splitRowLine1TwoCol: {
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  splitRowCategoryCol: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  splitRowCategoryStack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    minWidth: 0,
    flex: '1 1 auto',
  },
  categoryText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  splitRowClientProjectCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0,
    minWidth: 0,
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  splitRowAmountCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(0.5),
    justifySelf: 'end',
    minWidth: 0,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      alignItems: 'flex-end',
    },
  },
  splitRowAmountInline: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    flexWrap: 'nowrap',
  },
  /** Row 2: tags only */
  splitRowTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    width: '100%',
    minWidth: 0,
    paddingLeft: 0,
  },
  splitRowTagsSeries: {
    paddingLeft: 0,
  },
  /** Row 3: memo + expand */
  splitRowMemo: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    width: '100%',
    minWidth: 0,
    paddingLeft: 0,
  },
  splitRowMemoSeries: {
    paddingLeft: 0,
  },
  memoText: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  memoToggle: {
    alignSelf: 'flex-start',
    minWidth: 'max-content',
    padding: theme.spacing(0, 0, 0, 0.5),
    textTransform: 'none',
    fontSize: theme.typography.paragraphRegular.fontSize,
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: theme.typography.paragraphRegular.lineHeight,
  },
  memoLineClamp: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical' as const,
    WebkitLineClamp: 3,
    overflow: 'hidden',
  },
  exclusionCluster: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: theme.spacing(0.25, 0.5),
    borderRadius: 6,
    backgroundColor: theme.palette.background.default,
  },
  billableChip: {
    height: 24,
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    borderRadius: `${6}px`,
    backgroundColor: theme.palette.pending.light,
    color: theme.palette.pending.dark,
    '& .MuiChip-label': {
      fontSize: theme.typography.captionRegular.fontSize,
      lineHeight: 1.2,
      paddingLeft: theme.spacing(0.25),
      paddingRight: theme.spacing(0.25),
    },
    '& .MuiChip-icon': {
      marginLeft: 0,
      marginRight: 0,
      color: theme.palette.pending.dark,
    },
  },
  chip: {
    height: 28,
    maxWidth: 160,
    minWidth: 40,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default,
    borderRadius: 22,
    border: 'none',
  },
  inputField: {
    fontSize: 16,
    textAlign: 'right',
  },
  overflowAmount: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 6,
    color: theme.palette.text.secondary,
  },
  padding: {
    display: 'flex',
    padding: 0.5,
    flexShrink: 0,
  },
  infoIcon: {
    color: theme.palette.icon.light,
    marginLeft: 8,
    height: 18,
    width: 18,
  },
  excludeIcon: {
    height: theme.spacing(3),
    width: theme.spacing(3),
  },
  exclusionIconSmall: {
    width: 16,
    height: 16,
    display: 'block',
  },
  splitPadding: {
    padding: '0px 12px',
  },
  splitPaddingSeries: {
    padding: 0,
  },
  showAllButton: {
    width: '100%',
    paddingTop: 8,
    paddingBottom: 8,
  },
}));
